/**
 * The "Available Stock" table in a line's expanded row: one row per lead-time day of the line's
 * Items delivery-times response, showing the quantity available and the date it can be delivered.
 *
 * Position: rendered by the cart
 * (`components/ShoppingCart/ShoppingCartTable/ShoppingCartTableRowExpandedRender.tsx`),
 * quote details
 * (`components/QuoteDetails/Helpers/ViewProductsTableComponents/ViewTableExpandContent.tsx`,
 * `components/QuoteDetails/Helpers/EditProductsTableComponents/TableExpandContent.tsx`) and the
 * shopping-list price view
 * (`components/ShoppingListDetails/Helpers/ViewPrice/ViewPriceProductDetails.tsx`).
 * Each passes `leadTimeDays` from its delivery-times response, requested through
 * `components/Helper/leadTimesHelpers.ts`.
 *
 * Rule a change must keep: a day's quantity shows "TBD" rather than a number when the day has a
 * buyout lead time, its `availabilityPlant` has a vendor number, or it has no `availabilityPlant`
 * at all. Renders nothing when there are no lead-time days.
 *
 * Related: `unit-tests/shared/AvailableStock.spec.tsx`. The Excel product export applies the same
 * buyout / vendor test to its stock column in
 * `components/Shared/ExcelExport/Products/ProductsExportUtils.tsx` → `exportProductsToExcel`.
 */
import React from "react";
import { ILeadTimeDay } from "@ryerson/common.models";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@ryerson/frontend.table";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useApplication } from "@ryerson/frontend.application";
import { AvailableStockContent } from "./AvailableStockContent";

const Cell = styled(TableCell)`
	width: 50%;
`;

interface AvailabilityInfo {
	dateAvailable?: string;
	plant: number;
	displayQuantity: string;
}

/**
 * Renders the "Available Stock" table for one line, or nothing when `leadTimeDays` is empty.
 * `leadTimeDays` is the line's `IDeliveryTimesResponse.leadTimeDays`; `unitOfMeasure` is the
 * display label appended to each quantity; `topMargin` is in pixels.
 */
const AvailableStock: React.FC<{
	leadTimeDays?: ILeadTimeDay[];
	unitOfMeasure: string;
	topMargin?: number;
}> = ({ leadTimeDays, unitOfMeasure, topMargin }) => {
	const { theme } = useTheme();
	const { lighterGray, text, darkGray } = theme.colors.primary;
	const {
		localization: { language },
	} = useApplication();
	const content = AvailableStockContent[language];

	if (!leadTimeDays || leadTimeDays.length === 0) {
		return null;
	}

	/**
	 * Maps availability from deliverTimesResponse to a format easily used by the table
	 * If buyout lead time or vendorNo  exists, or the day has no availabilityPlant, quantity will
	 * be shown as TBD
	 *
	 */
	const availabilityData: AvailabilityInfo[] = leadTimeDays.map((item: ILeadTimeDay) => {
		const showQuantityTBD = !!(
			item.buyoutLeadTime ||
			item.availabilityPlant?.vendorNo ||
			!item.availabilityPlant
		);
		const quantity = showQuantityTBD
			? content.tbd
			: `${item.availabilityPlant?.quantityAvailable} ${unitOfMeasure}`;
		return {
			dateAvailable: item.deliveryDate,
			plant: item.availabilityPlant?.plant ?? 0,
			displayQuantity: quantity,
		};
	});

	const getAvailableStockRows = (availabilityData: AvailabilityInfo[]) => {
		return availabilityData.map((item: AvailabilityInfo, index: number) => {
			const showBottomBorder = index !== availabilityData.length - 1;
			return (
				<TableRow key={`${item.plant}-${index}`} showBorder={showBottomBorder}>
					<Cell>
						<Typography color={text}>{item.displayQuantity}</Typography>
					</Cell>
					<Cell>
						<Typography color={text}>{item.dateAvailable}</Typography>
					</Cell>
				</TableRow>
			);
		});
	};

	return (
		<div
			css={css`
				margin-top: ${topMargin || 0}px;
			`}
		>
			<Typography weight="bold">{content.availableStock}</Typography>
			<Table
				addStyleString={`
					margin-top: 10px;
					border: 1px solid ${theme.colors.primary.darkGray}; ;
					border-radius: 2px;`}
			>
				<TableHead>
					<TableRow backgroundColor={lighterGray}>
						<Cell>
							<Typography color={darkGray}>{content.quantity}</Typography>
						</Cell>
						<Cell>
							<Typography color={darkGray}>{content.dateAvailable}</Typography>
						</Cell>
					</TableRow>
				</TableHead>
				<TableBody>{getAvailableStockRows(availabilityData)}</TableBody>
			</Table>
		</div>
	);
};

export default AvailableStock;
